import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { notificationApiService } from "@/services/notificationApiService";
import { toast } from "@/components/ui/use-toast";
import { invalidateNotificationQueries } from "@/utils/queryCache";
import { useNotificationRealtime } from "@/hooks/useNotificationRealtime";

export interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: "info" | "warning" | "error" | "success";
  priority?: "urgent" | "high" | "medium" | "low";
  is_read: boolean;
  created_at: string;
  updated_at: string;
}

export interface UseOptimizedNotificationsOptions {
  /** When false, skips fetching the full notifications list (e.g. profile modal closed). */
  enabled?: boolean;
}

/**
 * Enhanced optimized hook for notifications with React Query caching, deduplication, and CRUD operations
 * This prevents duplicate API calls for notifications while maintaining all functionality
 */
export const useOptimizedNotifications = (options?: UseOptimizedNotificationsOptions) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useNotificationRealtime(user?.id);

  const {
    data: notifications = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: async (): Promise<Notification[]> => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      const data = await notificationApiService.fetchUserNotifications(user.id);

      return data || [];
    },
    enabled: !!user?.id && (options?.enabled ?? true),
    staleTime: 1 * 60 * 1000, // Consider data stale after 1 minute
    cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // Calculate unread count
  const unreadCount = notifications.filter((n) => !n.is_read).length;

  // If some other part of the app creates a notification, keep the cache fresh
  useEffect(() => {
    if (!user?.id) return;

    const handleNotificationCreated = () => {
      invalidateNotificationQueries(queryClient, user.id);
      refetch();
    };

    window.addEventListener("notification-created", handleNotificationCreated);
    return () => {
      window.removeEventListener("notification-created", handleNotificationCreated);
    };
  }, [queryClient, refetch, user?.id]);

  // Mark notification as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await notificationApiService.markAsRead(notificationId);
      return notificationId;
    },
    onSuccess: (notificationId) => {
      // Update the cache optimistically
      queryClient.setQueryData(["notifications", user?.id], (old: Notification[] = []) =>
        old.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      invalidateNotificationQueries(queryClient, user?.id);
    },
    onError: (error) => {
      toast({
        title: "Failed to mark notification as read",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    },
  });

  // Mark all notifications as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      await notificationApiService.markAllAsRead(user.id);
      return true;
    },
    onSuccess: () => {
      // Update the cache optimistically
      queryClient.setQueryData(["notifications", user?.id], (old: Notification[] = []) =>
        old.map((n) => ({ ...n, is_read: true }))
      );

      invalidateNotificationQueries(queryClient, user?.id);
      toast({
        title: "All notifications marked as read",
        description: "All notifications have been marked as read.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to mark all notifications as read",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    },
  });

  // Delete notification mutation
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await notificationApiService.deleteNotification(notificationId);
      return notificationId;
    },
    onSuccess: (notificationId) => {
      // Update the cache optimistically
      queryClient.setQueryData(["notifications", user?.id], (old: Notification[] = []) =>
        old.filter((n) => n.id !== notificationId)
      );
      invalidateNotificationQueries(queryClient, user?.id);
    },
    onError: (error) => {
      toast({
        title: "Failed to delete notification",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    },
  });

  // Handler functions that maintain the same interface
  const markAsRead = async (notificationId: string) => {
    await markAsReadMutation.mutateAsync(notificationId);
  };

  const markAllAsRead = async () => {
    await markAllAsReadMutation.mutateAsync();
  };

  const deleteNotification = async (notificationId: string) => {
    await deleteNotificationMutation.mutateAsync(notificationId);
  };

  const fetchNotifications = () => {
    refetch();
  };

  return {
    notifications,
    unreadCount,
    loading:
      isLoading ||
      markAsReadMutation.isPending ||
      markAllAsReadMutation.isPending ||
      deleteNotificationMutation.isPending,
    error: error?.message || null,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch,
  };
};
